// components
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { MlCtaCard } from '@/components/molecules';

// types
import { IOrThreeBlocksProps } from './or-three-blocks.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';

// utils + hooks
import classNames from 'classnames';
import { JSX, useMemo } from 'react';
import orThreeBlocksVariants from './or-three-blocks.variants';

/**
 * Renders a responsive CTA layout with one primary card and up to two complementary cards.
 *
 * - Always displays the `mainCard`, which anchors the layout.
 * - Accepts up to two `complementaryCards`; extras are trimmed via `slice(0, 2)`.
 * - Layout dynamically adjusts based on how many complementary cards are passed:
 *   - 0 → main card spans full width
 *   - 1 → main card and one complementary card each span half the width
 *   - 2 → all cards are arranged in a responsive grid
 *
 * - Optional top and bottom dividers can be enabled for visual separation.
 * - Uses Tailwind Variants (`orThreeBlocksVariants`) to apply spacing and layout logic.
 * - All cards are rendered using the `MlCtaCard` component.
 *
 */

export const OrThreeBlocks = ({
  mainCard,
  complementaryCards,
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
}: IOrThreeBlocksProps): JSX.Element => {
  // This component is capable of rendering only two complementary cards, so in case the user sends more, we make sure to trim them.
  const slicedComplementaryCards = useMemo(
    () => complementaryCards.slice(0, 2),
    [complementaryCards],
  );

  const { base, cardsContainer, mainCardStyles, complementaryCard } =
    orThreeBlocksVariants({
      complementaryCardsLength: slicedComplementaryCards.length.toString() as
        | '1'
        | '2',
      topSpacing,
      bottomSpacing,
    });

  return (
    // todo: update theme to dynamic val
    <div data-theme="light">
      <section data-testid="or-three-blocks" className={base()}>
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
        <div className={cardsContainer()}>
          <MlCtaCard
            {...mainCard}
            element="div"
            className={classNames(mainCardStyles(), {
              'md:col-span-12': slicedComplementaryCards.length === 0,
              'md:col-span-6': slicedComplementaryCards.length >= 1,
            })}
          />
          {slicedComplementaryCards.map((cardProps, i) => (
            <MlCtaCard
              {...cardProps}
              size={cardProps.size}
              element="div"
              key={`${i}_${cardProps.title}`}
              className={classNames(complementaryCard(), {
                'md:col-span-6': slicedComplementaryCards.length >= 1,
                'md:row-span-2': slicedComplementaryCards.length === 1,
              })}
              data-key={i}
            />
          ))}
        </div>

        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
      </section>
    </div>
  );
};
